import { useEffect, useState } from 'react';
import { CheckCircle2, ChevronLeft, ChevronRight, Loader2, Send } from 'lucide-react';
import { Button } from '../ui/Button';
import type { PropsCaptura } from './registro';
import { SobreSelector } from './SobreSelector';
import { PulsarParaHablar } from '../PulsarParaHablar';
import { useColaMedia } from '../../lib/colaMedia';
import { borrarGrabacion } from '../../lib/grabacionEnCurso';
import type { GrabacionAudio } from '../../hooks/useGrabadorAudio';
import { nombresOrdenados, normalizarOpciones } from '../../lib/sesion';
import type { CampoSchema, OpcionCampo, SeccionCaptura } from '../../lib/sesion';

/**
 * Rótulos con acento para campos extra comunes de manifiestos pre-v3 que aún
 * no declaran `etiqueta`. Un campo fuera del mapa usa su nombre capitalizado.
 */
const ROTULOS: Record<string, string> = {
  cuadrante: 'Cuadrante',
  aspecto: 'Aspecto',
  paso: 'Paso',
};

/** Etiquetas humanas para opciones literales de manifiestos pre-v3. */
const OPCIONES_HUMANAS: Record<string, string> = {
  dice: 'Lo dice',
  hace: 'Lo hace',
  piensa: 'Lo piensa',
  siente: 'Lo siente',
  conservar: 'Conservar',
  mejorar: 'Mejorar',
};

/** Rótulo del campo: `etiqueta` del manifiesto (v3) > mapa legado > capitalizado. */
function rotuloDe(nombre: string, campo?: CampoSchema): string {
  return (
    campo?.etiqueta ?? ROTULOS[nombre] ?? nombre.charAt(0).toUpperCase() + nombre.slice(1)
  );
}

/** Etiqueta de la opción: la del manifiesto (v3) > mapa legado > el valor crudo. */
function etiquetaDeOpcion(op: OpcionCampo): string {
  if (op.etiqueta !== op.valor) return op.etiqueta;
  return OPCIONES_HUMANAS[op.valor] ?? op.etiqueta;
}

/** Rótulo + ayuda del manifiesto sobre el control del campo (ADR-0005 §1). */
function Encabezado({ nombre, campo }: { nombre: string; campo?: CampoSchema }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="font-sans text-sm font-medium text-content">
        {rotuloDe(nombre, campo)}
      </span>
      {campo?.ayuda && (
        <span className="font-sans text-xs text-content-muted">{campo.ayuda}</span>
      )}
    </div>
  );
}

const CLASE_INPUT =
  'min-h-touch w-full rounded-md border border-border-strong bg-surface-raised px-3 py-2 text-base text-content placeholder:text-content-muted transition-colors duration-fast focus-visible:outline-none focus-visible:shadow-focus disabled:opacity-50';

/**
 * UN campo del `payload_schema`, dibujado declarativamente (D5c + ADR-0005):
 * string con `opciones` → botones de elección, `number` con min/max → fila de
 * escala (o input numérico si el rango no cabe a 380px), string largo →
 * textarea, string libre → input con rótulo visible (nunca una caja muda).
 * Exportado: CapturaVoto lo reusa para sus campos extra (Oleada 3).
 */
export function CampoDeclarativo({
  nombre,
  campo,
  valor,
  onCambiar,
  deshabilitado,
  filas = 3,
}: {
  nombre: string;
  campo: CampoSchema;
  valor: string | number | undefined;
  onCambiar: (v: string | number | undefined) => void;
  deshabilitado: boolean;
  /** Filas del textarea cuando el campo es largo (el principal usa 5). */
  filas?: number;
}) {
  const opciones = normalizarOpciones(campo.opciones);

  // string con opciones literales → botones de elección (uno activo).
  if (campo.tipo === 'string' && opciones.length) {
    return (
      <div className="flex flex-col gap-2">
        <Encabezado nombre={nombre} campo={campo} />
        <div
          className="grid gap-2"
          role="radiogroup"
          aria-label={rotuloDe(nombre, campo)}
          style={{
            gridTemplateColumns: `repeat(${Math.min(opciones.length, 2)}, minmax(0, 1fr))`,
          }}
        >
          {opciones.map((op) => {
            const elegido = valor === op.valor;
            return (
              <button
                key={op.valor}
                type="button"
                role="radio"
                aria-checked={elegido}
                disabled={deshabilitado}
                onClick={() => onCambiar(op.valor)}
                className={`flex min-h-touch items-center justify-center rounded-lg border px-3 font-sans text-base font-medium text-content transition-colors duration-fast focus-visible:outline-none focus-visible:shadow-focus disabled:opacity-50 ${
                  elegido
                    ? 'border-primary bg-surface-sunken'
                    : 'border-border-strong bg-surface-raised hover:border-primary hover:bg-surface-sunken'
                }`}
              >
                {etiquetaDeOpcion(op)}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // number con rango → fila de escala; con rango amplio (> 10), input numérico.
  if (campo.tipo === 'number') {
    const min = typeof campo.min === 'number' ? campo.min : 1;
    const max = typeof campo.max === 'number' ? campo.max : 5;
    if (max - min > 10) {
      return (
        <div className="flex flex-col gap-2">
          <Encabezado nombre={nombre} campo={campo} />
          <input
            type="number"
            inputMode="numeric"
            min={min}
            max={max}
            value={typeof valor === 'number' ? valor : ''}
            onChange={(e) => {
              const crudo = e.target.value;
              onCambiar(crudo === '' ? undefined : Number(crudo));
            }}
            placeholder={campo.placeholder}
            aria-label={rotuloDe(nombre, campo)}
            disabled={deshabilitado}
            className={CLASE_INPUT}
          />
        </div>
      );
    }
    const escala: number[] = [];
    for (let v = min; v <= max; v += 1) escala.push(v);
    return (
      <div className="flex flex-col gap-2">
        <Encabezado nombre={nombre} campo={campo} />
        <div
          className="grid gap-2"
          role="radiogroup"
          aria-label={rotuloDe(nombre, campo)}
          style={{ gridTemplateColumns: `repeat(${escala.length}, minmax(0, 1fr))` }}
        >
          {escala.map((v) => {
            const elegido = valor === v;
            return (
              <button
                key={v}
                type="button"
                role="radio"
                aria-checked={elegido}
                disabled={deshabilitado}
                onClick={() => onCambiar(v)}
                aria-label={`${rotuloDe(nombre, campo)} ${v}`}
                className={`flex min-h-touch items-center justify-center rounded-lg border font-display text-lg font-semibold text-content transition-colors duration-fast focus-visible:outline-none focus-visible:shadow-focus disabled:opacity-50 ${
                  elegido
                    ? 'border-primary bg-surface-sunken'
                    : 'border-border-strong bg-surface-raised hover:border-primary hover:bg-surface-sunken'
                }`}
              >
                {v}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // string libre largo (max >= 150) → textarea (p. ej. «espero»/«temo» del CCJ).
  if (typeof campo.max === 'number' && campo.max >= 150) {
    return (
      <div className="flex flex-col gap-2">
        <Encabezado nombre={nombre} campo={campo} />
        <textarea
          value={typeof valor === 'string' ? valor : ''}
          onChange={(e) => onCambiar(e.target.value)}
          rows={filas}
          placeholder={campo.placeholder}
          aria-label={rotuloDe(nombre, campo)}
          maxLength={campo.max}
          disabled={deshabilitado}
          className={CLASE_INPUT}
        />
      </div>
    );
  }

  // string libre → input simple, con su rótulo visible.
  return (
    <div className="flex flex-col gap-2">
      <Encabezado nombre={nombre} campo={campo} />
      <input
        type="text"
        value={typeof valor === 'string' ? valor : ''}
        onChange={(e) => onCambiar(e.target.value)}
        placeholder={campo.placeholder}
        aria-label={rotuloDe(nombre, campo)}
        maxLength={typeof campo.max === 'number' ? campo.max : undefined}
        disabled={deshabilitado}
        className={CLASE_INPUT}
      />
    </div>
  );
}

/** ¿El valor cumple el requerido (y el largo mínimo si es string)? */
function campoLleno(campo: CampoSchema, valor: string | number | undefined): boolean {
  if (typeof valor === 'number') return true;
  if (typeof valor !== 'string') return false;
  const largo = valor.trim().length;
  const minimo = typeof campo.min === 'number' ? campo.min : 1;
  return largo >= minimo;
}

/**
 * Captura de la primitiva `texto` (Bolsillo, 380px, D5c declarativa).
 *
 * Con `capturas[].secciones` declaradas (ADR-0005 §2) la captura es un WIZARD
 * por pasos («Vas en X de Y»), y si la Cabina fijó una ronda dirigida
 * (actividad.ronda) muestra SOLO esa sección y el aporte la registra
 * (payload.seccion). Sin secciones: el formulario plano en el orden declarado.
 */
export function CapturaTexto(props: PropsCaptura) {
  if (props.captura.secciones.length > 0) return <CapturaPorSecciones {...props} />;
  return <CapturaPlana {...props} />;
}

/* ------------------- Formulario plano (sin secciones) ------------------- */

function CapturaPlana({
  captura,
  deshabilitado,
  enviando,
  onEnviar,
  actividadId,
  selloEnvio,
}: PropsCaptura) {
  const ordenados = nombresOrdenados(captura.campos);
  // Ancla de evaluación (ADR-0005 §3): elegir «sobre cuál» es parte del aporte.
  const [sobre, setSobre] = useState<string | null>(null);
  const requiereSobre = captura.sobre && captura.opciones.length > 0;
  // Campo principal del textarea: `texto` si existe; si no, el primer string
  // libre (sin opciones literales); sin campos declarados, `texto` a secas.
  const principal =
    ordenados.length === 0
      ? 'texto'
      : ordenados.includes('texto')
        ? 'texto'
        : (ordenados.find(
            (n) => captura.campos[n].tipo === 'string' && !captura.campos[n].opciones?.length,
          ) ?? 'texto');
  // El principal siempre se dibuja: si no es un campo declarado (p. ej. el
  // manifiesto solo declara opciones), el textarea `texto` va al final.
  const posiciones = ordenados.includes(principal) ? ordenados : [...ordenados, principal];

  const [texto, setTexto] = useState('');
  const [valores, setValores] = useState<Record<string, string | number>>({});

  // Modo ráfaga: el envío aceptado limpia SOLO el texto — la casilla elegida
  // (espero/temo, cuadrante…) queda marcada para mandar la siguiente idea.
  useEffect(() => {
    if (selloEnvio) setTexto('');
  }, [selloEnvio]);

  const reglaPrincipal: CampoSchema = captura.campos[principal] ?? {};
  const minPrincipal = typeof reglaPrincipal.min === 'number' ? reglaPrincipal.min : 2;

  // Qué falta para poder enviar: el textarea con su largo mínimo y todo campo
  // extra requerido. La lista alimenta el aviso bajo el botón (ADR-0005 §1).
  // `faltantesExtras` (sin el principal) es también lo que falta para HABLAR:
  // la voz exime solo el texto — la transcripción lo llena.
  const faltantesExtras: string[] = [];
  if (requiereSobre && sobre === null) faltantesExtras.push('elegir sobre cuál');
  for (const nombre of posiciones) {
    if (nombre === principal) continue;
    const campo = captura.campos[nombre];
    if (campo.requerido === false) continue;
    const v = valores[nombre];
    const lleno = typeof v === 'number' || (typeof v === 'string' && v.length > 0);
    if (!lleno) faltantesExtras.push(rotuloDe(nombre, campo));
  }
  const faltantes = [...faltantesExtras];
  if (texto.trim().length < minPrincipal) {
    faltantes.splice(
      requiereSobre && sobre === null ? 1 : 0,
      0,
      reglaPrincipal.etiqueta ?? 'tu aporte',
    );
  }
  const completo = faltantes.length === 0;

  // Voz dentro del formato: los campos elegidos (sin el principal, que llega
  // por transcripción) acompañan al audio. Ver {@link VozEnFormato}.
  function camposParaVoz(): Record<string, unknown> {
    const campos: Record<string, unknown> = {};
    for (const nombre of posiciones) {
      if (nombre === principal) continue;
      const v = valores[nombre];
      if (v !== undefined && v !== '') campos[nombre] = v;
    }
    if (requiereSobre && sobre !== null) campos.sobre = sobre;
    return campos;
  }

  function enviar() {
    const payload: Record<string, unknown> = { [principal]: texto.trim() };
    for (const nombre of posiciones) {
      if (nombre === principal) continue;
      const v = valores[nombre];
      if (v !== undefined && v !== '') payload[nombre] = v;
    }
    if (requiereSobre && sobre !== null) payload.sobre = sobre;
    onEnviar(payload);
  }

  function campoPrincipal() {
    return (
      <div key={principal} className="flex flex-col gap-2">
        {reglaPrincipal.etiqueta && <Encabezado nombre={principal} campo={reglaPrincipal} />}
        <textarea
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          rows={5}
          placeholder={reglaPrincipal.placeholder ?? captura.placeholder}
          aria-label={reglaPrincipal.etiqueta ?? 'Tu aporte'}
          maxLength={typeof reglaPrincipal.max === 'number' ? reglaPrincipal.max : undefined}
          className={CLASE_INPUT}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {requiereSobre && (
        <SobreSelector
          opciones={captura.opciones}
          valor={sobre}
          onElegir={setSobre}
          deshabilitado={enviando || deshabilitado}
        />
      )}
      {posiciones.map((nombre) =>
        nombre === principal ? (
          campoPrincipal()
        ) : (
          <CampoDeclarativo
            key={nombre}
            nombre={nombre}
            campo={captura.campos[nombre]}
            valor={valores[nombre]}
            onCambiar={(v) =>
              setValores((prev) => {
                if (v === undefined) {
                  const { [nombre]: _omitido, ...resto } = prev;
                  return resto;
                }
                return { ...prev, [nombre]: v };
              })
            }
            deshabilitado={enviando || deshabilitado}
          />
        ),
      )}
      <Button
        variant="primary"
        fullWidth
        loading={enviando}
        disabled={!completo || deshabilitado}
        onClick={enviar}
        leadingIcon={<Send className="h-5 w-5" aria-hidden="true" />}
      >
        Enviar mi aporte
      </Button>
      {!completo && (
        <p className="font-sans text-sm text-content-muted" aria-live="polite">
          Falta: {faltantes.join(', ')}
        </p>
      )}

      {/* Voz dentro del formato: mismas categorías, dicho en vez de escrito. */}
      <VozEnFormato
        actividadId={actividadId}
        faltantes={faltantesExtras}
        camposParaVoz={camposParaVoz}
        deshabilitado={deshabilitado || enviando}
      />
    </div>
  );
}

/* ------------------- Voz dentro del formato (compartido) ----------------- */

/**
 * Bloque «decilo con tu voz» de una captura de texto (plana o por secciones).
 *
 * El participante elige sus categorías y GRABA en vez de escribir: el audio
 * sube con los campos elegidos (subirMedia extra.campos, ADR-0005 §5) y la
 * transcripción del worker se vuelve el texto de esa casilla.
 *
 * Acá vive EL grabador grande (PulsarParaHablar, anillo + candado): cuando la
 * herramienta tiene formato, hablar ES hablarle al formato — no hay un segundo
 * grabador suelto que ignore la categoría elegida (bug real de la demo del
 * 1-ago: el usuario eligió «lo que dice», grabó en el bloque de evidencia y el
 * cuadrante se perdió). `faltantes` (los requeridos no-principales sin llenar)
 * bloquea el gesto CON explicación al presionar; el estado de subida vive en
 * una ranura fija para no empujar el formulario.
 */
function VozEnFormato({
  actividadId,
  faltantes,
  camposParaVoz,
  deshabilitado,
  onEnviado,
}: {
  actividadId?: string;
  faltantes: string[];
  /** Se evalúa AL GRABAR: los campos ya elegidos que acompañan al audio. */
  camposParaVoz: () => Record<string, unknown>;
  deshabilitado: boolean;
  /** Voz subida OK: el wizard marca su sección como resuelta y avanza. */
  onEnviado?: () => void;
}) {
  type EstadoVoz = 'subiendo' | 'en_cola' | 'enviado' | 'error';
  const [voz, setVoz] = useState<{ estado: EstadoVoz; error?: string } | null>(null);

  if (!actividadId) return null;

  /**
   * La voz pasa por la COLA DURABLE: queda en IndexedDB antes de tocar la red.
   * Si el envío no entra ahora, NO se pierde — la cola reintenta sola y el
   * aporte conserva los campos del formato y la hora real de la grabación.
   */
  async function aportar(g: GrabacionAudio) {
    setVoz({ estado: 'subiendo' });
    // El idem ES el id de la grabación (dedup seguro de la recuperación).
    const idem = g.id;
    const entregado = await useColaMedia.getState().encolarYEnviar({
      idem,
      actividadId: actividadId!,
      tipo: 'audio',
      blob: g.blob,
      filename: `nota-de-voz.${g.ext}`,
      mime: g.mime,
      inicio: g.inicioISO,
      fin: g.finISO,
      duracionS: g.duracionS,
      campos: camposParaVoz(),
    });
    // Ya está a salvo en la cola de envío: los trozos pueden liberarse.
    void borrarGrabacion(idem);
    if (entregado) {
      setVoz({ estado: 'enviado' });
      onEnviado?.();
      return;
    }
    const enCola = useColaMedia.getState().items.find((i) => i.idem === idem);
    if (enCola?.bloqueado) {
      setVoz({ estado: 'error', error: enCola.bloqueado });
    } else {
      setVoz({ estado: 'en_cola' });
      // La grabación ya está a salvo y el wizard puede seguir: el acta la
      // recibirá igual, con la hora en que se dijo.
      onEnviado?.();
    }
  }

  return (
    <div className="flex flex-col gap-1.5">
      <p className="mt-1 font-sans text-sm font-semibold text-content">O decilo con tu voz</p>
      <PulsarParaHablar
        actividadId={actividadId}
        onListo={(g) => void aportar(g)}
        bloqueado={
          faltantes.length > 0
            ? `Para hablar, primero: ${faltantes.join(', ')}.`
            : voz?.estado === 'subiendo'
              ? 'Estamos subiendo tu voz anterior. Un momento…'
              : deshabilitado
                ? 'La actividad no está recibiendo aportes en este momento.'
                : null
        }
      />
      {/* Ranura fija de estado: aparece sin empujar el formulario. */}
      <div className="min-h-5" aria-live="polite">
        {faltantes.length > 0 ? (
          <p className="font-sans text-xs text-content-muted">
            Para hablar, primero: {faltantes.join(', ')}.
          </p>
        ) : voz?.estado === 'subiendo' ? (
          <p className="flex items-center gap-1.5 font-sans text-xs text-content-muted">
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
            Subiendo tu voz…
          </p>
        ) : voz?.estado === 'enviado' ? (
          <p className="flex items-center gap-1.5 font-sans text-xs font-semibold text-success">
            <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
            Quedó en el acta — se transcribe sola. Podés decir otra.
          </p>
        ) : voz?.estado === 'error' ? (
          <p role="alert" className="font-sans text-xs text-error">
            {voz.error}
          </p>
        ) : null}
      </div>
    </div>
  );
}

/* --------------- Wizard por secciones / ronda dirigida ------------------ */

function CapturaPorSecciones({
  captura,
  deshabilitado,
  enviando,
  onEnviar,
  ronda,
  actividadId,
}: PropsCaptura) {
  const secciones = captura.secciones;
  // Ronda dirigida: la Cabina fijó la sección; el wizard queda en ese paso.
  const dirigida: SeccionCaptura | null =
    ronda != null ? (secciones.find((s) => s.id === ronda) ?? null) : null;

  const [paso, setPaso] = useState(0);
  const [valores, setValores] = useState<Record<string, string | number>>({});
  // Un aporte es texto O voz: la sección dicha ya viajó como aporte propio.
  // Queda RESUELTA (✓, sin exigir sus casillas) y el envío escrito declara
  // `secciones_habladas` para que el backend no las pida de nuevo.
  const [habladas, setHabladas] = useState<ReadonlySet<string>>(new Set());

  const actual = dirigida ?? secciones[Math.min(paso, secciones.length - 1)];
  const camposDe = (s: SeccionCaptura) => s.campos.filter((n) => captura.campos[n]);

  const faltantesDe = (s: SeccionCaptura): string[] =>
    habladas.has(s.id)
      ? []
      : camposDe(s)
          .filter((n) => {
            const campo = captura.campos[n];
            if (campo.requerido === false) return false;
            return !campoLleno(campo, valores[n]);
          })
          .map((n) => rotuloDe(n, captura.campos[n]));

  /** El campo que llena la transcripción en ESTA sección (espejo del backend). */
  const principalDe = (s: SeccionCaptura): string => {
    const nombres = camposDe(s);
    if (nombres.includes('texto')) return 'texto';
    return (
      nombres.find(
        (n) =>
          captura.campos[n].tipo === 'string' &&
          !normalizarOpciones(captura.campos[n].opciones).length,
      ) ?? 'texto'
    );
  };

  const faltanAqui = faltantesDe(actual);
  // Para enviar: en ronda dirigida solo debe SU sección; a ritmo propio, todas.
  const faltanParaEnviar = dirigida
    ? faltanAqui
    : secciones.flatMap((s) => faltantesDe(s));
  const esUltimo = dirigida !== null || paso >= secciones.length - 1;

  // ── Voz dentro del formato, POR SECCIÓN ─────────────────────────────────
  // Hablar aporta LA SECCIÓN visible (payload.seccion): en «espero/temo», la
  // voz del paso «temo» exime `temo` — su texto llega por transcripción. Los
  // demás requeridos de la sección (categorías) se exigen igual que escribiendo.
  const nombresSeccion = camposDe(actual);
  const principalSeccion = principalDe(actual);
  const faltantesVozSeccion = nombresSeccion
    .filter((n) => {
      if (n === principalSeccion) return false;
      const campo = captura.campos[n];
      if (campo.requerido === false) return false;
      return !campoLleno(campo, valores[n]);
    })
    .map((n) => rotuloDe(n, captura.campos[n]));

  function camposParaVozSeccion(): Record<string, unknown> {
    const campos: Record<string, unknown> = { seccion: actual.id };
    for (const nombre of nombresSeccion) {
      if (nombre === principalSeccion) continue;
      const v = valores[nombre];
      if (v !== undefined && v !== '') campos[nombre] = v;
    }
    return campos;
  }

  /** Voz subida OK (a ritmo propio): sección resuelta y el wizard avanza solo. */
  function vozEnviada() {
    if (dirigida) return; // en ronda dirigida no hay pasos que avanzar
    const id = actual.id;
    setHabladas((prev) => new Set(prev).add(id));
    setPaso((p) => Math.min(secciones.length - 1, p + 1));
  }

  const actualHablada = !dirigida && habladas.has(actual.id);
  const todoPorVoz = !dirigida && secciones.every((s) => habladas.has(s.id));

  function reiniciar() {
    setPaso(0);
    setValores({});
    setHabladas(new Set());
  }

  function enviar() {
    const payload: Record<string, unknown> = {};
    if (dirigida) {
      for (const nombre of camposDe(dirigida)) {
        const v = valores[nombre];
        if (v !== undefined && v !== '') payload[nombre] = v;
      }
      payload.seccion = dirigida.id;
      onEnviar(payload);
      return;
    }
    for (const s of secciones) {
      const principalS = principalDe(s);
      for (const nombre of camposDe(s)) {
        // Lo dicho no se reenvía escrito: el principal de una sección hablada
        // viaja en su aporte de voz; sus casillas cortas (tecleadas antes de
        // hablar) sí acompañan, para que el tablero agrupe igual.
        if (habladas.has(s.id) && nombre === principalS) continue;
        const v = valores[nombre];
        if (v !== undefined && v !== '') payload[nombre] = v;
      }
    }
    if (habladas.size > 0) payload.secciones_habladas = [...habladas];
    onEnviar(payload);
  }

  return (
    <div className="flex flex-col gap-4">
      {!dirigida && secciones.length > 1 && (
        <div className="flex flex-col gap-1.5">
          <p className="font-sans text-sm text-content-muted" aria-live="polite">
            Vas en {paso + 1} de {secciones.length}
          </p>
          <div className="flex gap-1" aria-hidden="true">
            {secciones.map((s, i) => (
              <span
                key={s.id}
                className={`h-1.5 flex-1 rounded-full transition-colors duration-fast ${
                  i <= paso ? 'bg-primary' : 'bg-surface-sunken'
                }`}
              />
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-col gap-1">
        <h2 className="font-display text-lg font-semibold text-content">{actual.titulo}</h2>
        {actual.ayuda && (
          <p className="font-sans text-sm text-content-muted">{actual.ayuda}</p>
        )}
      </div>

      {/* Paso ya dicho: sus casillas no se piden de nuevo — texto O voz. */}
      {actualHablada && (
        <p
          className="flex items-center gap-1.5 font-sans text-sm font-semibold text-success"
          aria-live="polite"
        >
          <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden="true" />
          Este paso ya quedó con tu voz — la casilla se llena sola con la
          transcripción.
        </p>
      )}

      {camposDe(actual).map((nombre) => (
        <CampoDeclarativo
          key={nombre}
          nombre={nombre}
          campo={captura.campos[nombre]}
          valor={valores[nombre]}
          onCambiar={(v) =>
            setValores((prev) => {
              if (v === undefined) {
                const { [nombre]: _omitido, ...resto } = prev;
                return resto;
              }
              return { ...prev, [nombre]: v };
            })
          }
          deshabilitado={enviando || deshabilitado || actualHablada}
        />
      ))}

      {todoPorVoz ? (
        <div className="flex flex-col gap-2">
          <p
            className="flex items-center gap-1.5 font-sans text-sm font-semibold text-success"
            aria-live="polite"
          >
            <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden="true" />
            Todo tu aporte quedó con tu voz — no hay nada más que enviar.
          </p>
          <Button
            variant="secondary"
            fullWidth
            onClick={reiniciar}
            disabled={deshabilitado}
          >
            Aportar otro
          </Button>
        </div>
      ) : (
        <div className="flex gap-2">
          {!dirigida && paso > 0 && (
            <Button
              variant="secondary"
              onClick={() => setPaso((p) => Math.max(0, p - 1))}
              disabled={enviando || deshabilitado}
              leadingIcon={<ChevronLeft className="h-5 w-5" aria-hidden="true" />}
            >
              Atrás
            </Button>
          )}
          {esUltimo ? (
            <Button
              variant="primary"
              fullWidth
              loading={enviando}
              disabled={faltanParaEnviar.length > 0 || deshabilitado}
              onClick={enviar}
              leadingIcon={<Send className="h-5 w-5" aria-hidden="true" />}
            >
              Enviar mi aporte
            </Button>
          ) : (
            <Button
              variant="primary"
              fullWidth
              disabled={faltanAqui.length > 0 || enviando || deshabilitado}
              onClick={() => setPaso((p) => Math.min(secciones.length - 1, p + 1))}
              leadingIcon={<ChevronRight className="h-5 w-5" aria-hidden="true" />}
            >
              Siguiente
            </Button>
          )}
        </div>
      )}
      {!todoPorVoz && (esUltimo ? faltanParaEnviar : faltanAqui).length > 0 && (
        <p className="font-sans text-sm text-content-muted" aria-live="polite">
          Falta: {(esUltimo ? faltanParaEnviar : faltanAqui).join(', ')}
        </p>
      )}

      {/* Voz por sección: hablar aporta el paso visible y lo deja resuelto.
          key por sección: el estado del grabador no arrastra al paso siguiente. */}
      {!actualHablada && !todoPorVoz && (
        <VozEnFormato
          key={actual.id}
          actividadId={actividadId}
          faltantes={faltantesVozSeccion}
          camposParaVoz={camposParaVozSeccion}
          deshabilitado={deshabilitado || enviando}
          onEnviado={vozEnviada}
        />
      )}
    </div>
  );
}
